import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, FileText, BookOpen, CornerDownLeft, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { PAGES, SUGGESTED_PAGES, searchAll, type PageEntry } from "./searchIndex";
import type { Article } from "@/data/articles";
import { getBlogCategoryForArticle } from "@/data/blogCategories";

type Props = {
  open: boolean;
  onClose: () => void;
};

type FlatItem =
  | { kind: "page"; item: PageEntry }
  | { kind: "article"; item: Article };

export const SearchDialog = ({ open, onClose }: Props) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
    } else {
      // focus after paint
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const { pages, articles, isEmpty } = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      return { pages: SUGGESTED_PAGES, articles: [] as Article[], isEmpty: true };
    }
    const res = searchAll(trimmed);
    return { pages: res.pages, articles: res.articles, isEmpty: false };
  }, [query]);

  const flat: FlatItem[] = useMemo(() => {
    const p: FlatItem[] = pages.map((item) => ({ kind: "page", item }));
    const a: FlatItem[] = articles.map((item) => ({ kind: "article", item }));
    return [...p, ...a];
  }, [pages, articles]);

  // Clamp activeIndex when list changes
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (activeIndex >= flat.length) setActiveIndex(Math.max(0, flat.length - 1));
  }, [flat.length, activeIndex]);

  const openItem = (item: FlatItem) => {
    const url = item.kind === "page" ? item.item.url : `/blog/${item.item.slug}`;
    onClose();
    navigate(url);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = flat[activeIndex];
      if (it) openItem(it);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  // Scroll active into view
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[10vh] md:pt-[12vh] bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Cerca nel sito"
    >
      <div
        className="w-full max-w-2xl bg-background border border-gold/30 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gold/15">
          <Search size={20} className="text-gold flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca pagine, articoli, argomenti…"
            className="flex-1 bg-transparent outline-none font-body text-base text-foreground placeholder:text-muted-foreground"
            aria-label="Campo di ricerca"
          />
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-gold transition-colors"
            aria-label="Chiudi ricerca"
          >
            <X size={20} />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-3">
          {isEmpty && (
            <div className="px-5 pb-2">
              <p className="font-body text-[11px] tracking-[0.14em] uppercase text-muted-foreground mb-3">
                Suggerimenti
              </p>
            </div>
          )}

          {pages.length > 0 && (
            <>
              <SectionLabel>{isEmpty ? "Pagine consigliate" : "Pagine"}</SectionLabel>
              <div className="px-2">
                {pages.map((p, i) => {
                  const idx = i;
                  return (
                    <ResultRow
                      key={`page-${p.url}`}
                      idx={idx}
                      active={activeIndex === idx}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => openItem({ kind: "page", item: p })}
                      icon={<FileText size={18} className="text-gold" />}
                      title={p.title}
                      subtitle={p.description}
                    />
                  );
                })}
              </div>
            </>
          )}

          {articles.length > 0 && (
            <>
              <SectionLabel>Articoli del blog</SectionLabel>
              <div className="px-2">
                {articles.map((a, i) => {
                  const idx = pages.length + i;
                  const cat = getBlogCategoryForArticle(a.category);
                  return (
                    <ResultRow
                      key={`article-${a.id}`}
                      idx={idx}
                      active={activeIndex === idx}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => openItem({ kind: "article", item: a })}
                      icon={<BookOpen size={18} className="text-gold" />}
                      title={a.title}
                      subtitle={a.excerpt}
                      badge={cat?.shortLabel}
                    />
                  );
                })}
              </div>
            </>
          )}

          {!isEmpty && pages.length === 0 && articles.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="font-body text-sm text-muted-foreground">
                Nessun risultato per <span className="text-foreground font-medium">"{query}"</span>.
              </p>
              <p className="font-body text-xs text-muted-foreground mt-2">
                Prova con parole come "bambini", "costi", "cintura", "agonisti".
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-5 py-3 border-t border-gold/15 bg-black/40">
          <FooterKey>
            <ArrowUp size={12} />
            <ArrowDown size={12} />
          </FooterKey>
          <span className="font-body text-[11px] text-muted-foreground">naviga</span>
          <FooterKey>
            <CornerDownLeft size={12} />
          </FooterKey>
          <span className="font-body text-[11px] text-muted-foreground">apri</span>
          <FooterKey>esc</FooterKey>
          <span className="font-body text-[11px] text-muted-foreground">chiudi</span>
        </div>
      </div>
    </div>
  );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="px-5 pt-3 pb-2 font-body text-[11px] tracking-[0.14em] uppercase text-muted-foreground">
    {children}
  </p>
);

type RowProps = {
  idx: number;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  badge?: string;
};

const ResultRow = ({ idx, active, onMouseEnter, onClick, icon, title, subtitle, badge }: RowProps) => (
  <button
    type="button"
    data-idx={idx}
    onMouseEnter={onMouseEnter}
    onClick={onClick}
    className={cn(
      "w-full text-left flex items-start gap-3 px-3 py-3 rounded-lg transition-colors",
      active ? "bg-gold/10 border border-gold/30" : "border border-transparent hover:bg-gold/5"
    )}
  >
    <span className="mt-0.5 flex-shrink-0">{icon}</span>
    <span className="flex-1 min-w-0">
      <span className="flex items-center gap-2">
        <span className="font-display text-base font-semibold text-foreground truncate">{title}</span>
        {active && <CornerDownLeft size={14} className="text-gold flex-shrink-0" />}
      </span>
      {subtitle && (
        <span className="block font-body text-sm text-muted-foreground line-clamp-2 mt-0.5">{subtitle}</span>
      )}
      {badge && (
        <span className="inline-block mt-2 font-body text-[10px] tracking-[0.12em] uppercase text-gold">
          {badge}
        </span>
      )}
    </span>
  </button>
);

const FooterKey = ({ children }: { children: React.ReactNode }) => (
  <kbd className="inline-flex items-center gap-0.5 px-2 py-1 rounded-md border border-gold/20 bg-black/60 font-body text-[10px] text-muted-foreground">
    {children}
  </kbd>
);
